import { formatDateSeparator } from "../../utils/time";

export interface ListGroup<T> {
  readonly key: string;
  readonly label: string;
  readonly items: readonly T[];
  readonly date?: Date;
  /** When true, items are pinned — rendered without a separator header. */
  readonly pinned?: boolean;
}

/**
 * Group items by date (day). Pinned items go first (no separator).
 * Items without a date go into an "Other" group at the end.
 * Groups sorted newest-first.
 */
export function groupByDate<T>(
  items: readonly T[],
  getDate: (item: T) => Date | null,
  isPinned?: (item: T) => boolean,
): readonly ListGroup<T>[] {
  const pinned: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (isPinned?.(item)) {
      pinned.push(item);
    } else {
      rest.push(item);
    }
  }

  const map = new Map<string, { date: Date; items: T[] }>();
  const noDate: T[] = [];

  for (const item of rest) {
    const d = getDate(item);
    if (!d || Number.isNaN(d.getTime())) {
      noDate.push(item);
      continue;
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      map.set(key, { date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [item] });
    }
  }

  const groups: ListGroup<T>[] = [];

  if (pinned.length > 0) {
    groups.push({ key: "_pinned", label: "Pinned", items: pinned, pinned: true });
  }

  groups.push(
    ...[...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, { date, items: groupItems }]) => ({
        key,
        label: formatDateSeparator(date).label,
        date,
        items: groupItems,
      })),
  );

  if (noDate.length > 0) {
    groups.push({ key: "_no_date", label: "Other", items: noDate });
  }

  return groups;
}

/**
 * Group items by first letter of a sort key. Pinned items go first (no separator).
 * "#" for non-alphabetic. Groups sorted A-Z, with "#" at the end.
 */
export function groupByLetter<T>(
  items: readonly T[],
  getSortKey: (item: T) => string,
  isPinned?: (item: T) => boolean,
): readonly ListGroup<T>[] {
  const pinned: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (isPinned?.(item)) {
      pinned.push(item);
    } else {
      rest.push(item);
    }
  }

  const map = new Map<string, T[]>();

  for (const item of rest) {
    const raw = getSortKey(item).trim();
    const first = raw[0]?.toUpperCase() ?? "#";
    const letter = /[A-ZА-ЯЁ]/i.test(first) ? first : "#";
    const existing = map.get(letter);
    if (existing) {
      existing.push(item);
    } else {
      map.set(letter, [item]);
    }
  }

  const groups: ListGroup<T>[] = [];

  if (pinned.length > 0) {
    groups.push({ key: "_pinned", label: "Pinned", items: pinned, pinned: true });
  }

  groups.push(
    ...[...map.entries()]
      .sort(([a], [b]) => {
        if (a === "#") return 1;
        if (b === "#") return -1;
        return a.localeCompare(b);
      })
      .map(([letter, groupItems]) => ({
        key: letter,
        label: letter,
        items: groupItems,
      })),
  );

  return groups;
}
